<template>
  <PeppolContainer :loading="saving">
    <div class="flex flex-col gap-3">
      <div class="grid-form items-end">
        <SelectComponent
          v-model="integrator"
          name="integrator"
          :label="$t('global.peppol.integrator')"
          :values="integratorOptions"
          :none-selected-label="$t('global.peppol.no_integrator')"
          display-key="name"
          :edition-mode="canEdit"
          :disabled="!canEdit"
        />
        <SelectComponent
          v-if="integrator"
          v-model="sendMode"
          name="send_mode"
          :label="$t('global.peppol.send_mode')"
          :values="sendModeOptions"
          display-key="name"
          :edition-mode="canEdit"
          :disabled="!canEdit"
        />
      </div>
      <!-- Without the partner's credentials on this environment the back
           answers 422: the option is greyed out instead. -->
      <InfoPanel
        v-if="!bcSolutionsAvailable"
        type="info"
        :description="$t('global.peppol.bcsolutions_unavailable')"
      />
      <div v-if="canEdit && changed">
        <BaseButton
          :title="$t('button.save')"
          icon="paper-plane"
          color="primary"
          @click="save"
        />
      </div>

      <PeppolRegisterBcSolutionsComponent
        v-if="company.peppol_integrator === 'bcsolutions'"
        :key="company.id"
        :company="company"
      />
    </div>
  </PeppolContainer>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'

import InfoPanel from '@c/baseComponents/InfoPanel.vue'
import SelectComponent from '@c/BaseFormComponent/SelectComponent.vue'
import PeppolContainer from '@c/Partner/Peppol/PeppolContainer.vue'
import PeppolRegisterBcSolutionsComponent from '@c/Partner/Peppol/PeppolRegisterBcSolutionsComponent.vue'

/**
 * A company's Peppol settings (integrator, automatic sending) and its
 * BCSolutions registration, after FM's PeppolProviderSelectorComponent.
 */
export default {
  name: 'PeppolCompanyComponent',
  components: {
    InfoPanel,
    PeppolContainer,
    PeppolRegisterBcSolutionsComponent,
    SelectComponent
  },
  props: {
    company: { type: Object, required: true }
  },
  data() {
    return { integrator: null, sendMode: 'manual', saving: false }
  },
  computed: {
    ...mapGetters({ metadata: 'company/getCompanyMetadata' }),
    canEdit() {
      return this.$can('manage_content')
    },
    // Strictly true: an API without the field keeps the option off.
    bcSolutionsAvailable() {
      return this.company.peppol_bcsolutions_available === true
    },
    integratorOptions() {
      const integrators = this.metadata?.peppol_integrators ?? ['bcsolutions']
      return [
        { id: null, name: this.$t('global.peppol.no_integrator') },
        ...integrators.map((id) => ({
          id,
          name: this.$t(`enums.peppol_integrator.${id}`),
          disabled: id === 'bcsolutions' && !this.bcSolutionsAvailable
        }))
      ].filter((option) => !option.disabled || option.id === this.integrator)
    },
    sendModeOptions() {
      const modes = this.metadata?.peppol_send_modes ?? [
        'manual',
        'when_established'
      ]
      return modes.map((id) => ({
        id,
        name: this.$t(`enums.peppol_send_mode.${id}`)
      }))
    },
    changed() {
      return (
        this.integrator !== (this.company.peppol_integrator ?? null) ||
        (this.integrator &&
          this.sendMode !== (this.company.peppol_send_mode ?? 'manual'))
      )
    }
  },
  watch: {
    company: {
      immediate: true,
      handler(company) {
        this.integrator = company.peppol_integrator ?? null
        this.sendMode = company.peppol_send_mode ?? 'manual'
      }
    }
  },
  methods: {
    ...mapActions({ updateCompanyPeppol: 'company/updateCompanyPeppol' }),
    save() {
      this.saving = true
      this.updateCompanyPeppol({
        id: this.company.id,
        integrator: this.integrator,
        send_mode: this.sendMode
      })
        .then(() => {
          this.$h.toastSuccess(
            this.$t('toasts.updated_f', { var: this.$t('global.company') })
          )
        })
        .catch(() => {})
        .finally(() => {
          this.saving = false
        })
    }
  }
}
</script>
